import React from 'react';
import PropTypes from 'prop-types';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import TodoProductItem from '@/screens/sales-activity/components/todo-list/on-going/product/TodoProductItem';
import { Skeleton, productKeyExtractor } from './TodoVisibilityUpdate.utils';
import Colors from '@global/colors';
import { ARROW_SIZE } from '@/constants';

const BrandItem = ({
  data,
  index,
  isSearch,
  onExpand,
  selectedBrand,
  loading,
  listProduct,
  limit,
  empty,
  loadingMore,
  skip,
  onSearchProducts,
  lastPayload,
  detailData,
  setLastPayload: handleProductUpdate,
  customerAddress,
  navigation,
  accountGroupId = null,
  isValueSellOutOnly = false,
}) => {
  const isExpanded = selectedBrand.indexOf(data._id) > -1;
  const products = listProduct[data._id];
  const isLoading = loading[data._id];
  const hasProducts = products?.length > 0;
  const isBrandEmpty = products != undefined && products.length === 0;
  const isEmptyConfirmed = empty?.[data._id] === true;

  return (
    <View
      testID={`todo-visibility-brand-item-${index}`}
      key={`brand-${index}`}
      style={styles.container}>
      <TouchableOpacity
        testID={`todo-visibility-brand-expand-${index}`}
        activeOpacity={isSearch ? 1 : 0.6}
        style={[styles.brandItem, isExpanded && styles.brandItemExpanded]}
        onPress={() => onExpand(data._id)}>
        <View style={styles.brandInfo}>
          <SociollaText type="bold" style={styles.brandText}>
            {data.name}
          </SociollaText>
        </View>
        <View
          style={[
            styles.iconWrapper,
            isExpanded && styles.iconWrapperExpanded,
          ]}>
          <Image
            style={ARROW_SIZE}
            width={ARROW_SIZE.width}
            height={ARROW_SIZE.height}
            svg={true}
            source={require('@assets/img/icons/arrow-down-grey.svg')}
          />
        </View>
      </TouchableOpacity>

      {hasProducts ? (
        isExpanded ? (
          <View style={styles.contentWrapper}>
            {products.map((pr, productIndex) => (
              <View
                key={productKeyExtractor(pr, productIndex)}
                style={styles.productItemWrapper}>
                <TodoProductItem
                  item={pr}
                  selectedProduct={lastPayload}
                  activityId={detailData?._id}
                  onChange={(val) => {
                    if (handleProductUpdate) {
                      handleProductUpdate(val);
                    }
                  }}
                  customerAddress={customerAddress}
                  navigation={navigation}
                  accountGroupId={accountGroupId}
                  isValueSellOutOnly={isValueSellOutOnly}
                />
              </View>
            ))}
            {products.length >= limit &&
            !isSearch &&
            !empty[data._id] &&
            !loadingMore[data._id] ? (
              <TouchableOpacity
                testID={`todo-visibility-brand-load-more-${index}`}
                style={styles.loadMore}
                activeOpacity={0.7}
                onPress={() => {
                  const newSkip = (skip[data._id] ?? 0) + limit;
                  onSearchProducts({
                    keyword: '',
                    brand_id: data._id,
                    product_skip: newSkip,
                  });
                }}>
                <SociollaText type="bold" style={styles.loadMoreText}>
                  Load More Products
                </SociollaText>
              </TouchableOpacity>
            ) : loadingMore[data._id] ? (
              <View style={styles.skeletonWrapper}>
                <Skeleton width={50} height={50} style={styles.skeletonImage} />
                <View style={styles.skeletonLines}>
                  <Skeleton width="70%" height={14} />
                  <Skeleton width="40%" height={12} />
                  <Skeleton width="90%" height={12} />
                </View>
              </View>
            ) : null}
          </View>
        ) : null
      ) : isLoading ? (
        <View style={styles.contentWrapper}>
          <View style={styles.skeletonWrapper}>
            <Skeleton width={50} height={50} style={styles.skeletonImage} />
            <View style={styles.skeletonLines}>
              <Skeleton width="70%" height={14} />
              <Skeleton width="40%" height={12} />
              <Skeleton width="90%" height={12} />
            </View>
          </View>
        </View>
      ) : isBrandEmpty && isExpanded && isEmptyConfirmed ? (
        <View style={styles.productEmpty}>
          <Image
            source={require('@assets/img/icons/no-product.svg')}
            width={60}
            height={60}
            svg={true}
            style={styles.emptyIcon}
          />
          <SociollaText style={styles.productEmptyText}>
            No products available for this brand
          </SociollaText>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    backgroundColor: Colors.white,
    marginHorizontal: 12,
    borderRadius: 6,
    elevation: 3,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: Colors.grey10,
  },
  brandItem: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Colors.white,
  },
  brandItemExpanded: {
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey10,
    backgroundColor: Colors.grey5,
  },
  brandInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  brandText: {
    fontSize: 15,
    color: Colors.black1,
    textTransform: 'capitalize',
    marginRight: 10,
  },
  iconWrapper: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
  },
  iconWrapperExpanded: {
    transform: [{ rotate: '-180deg' }],
  },
  contentWrapper: {
    backgroundColor: Colors.white,
  },
  productItemWrapper: {
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey5,
  },
  skeletonWrapper: {
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  skeletonImage: {
    marginRight: 15,
    borderRadius: 8,
  },
  skeletonLines: {
    flex: 1,
    gap: 8,
  },
  loadMore: {
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.grey5,
    margin: 16,
    borderRadius: 8,
  },
  loadMoreText: {
    color: Colors.blue0,
    fontSize: 13,
  },
  productEmpty: {
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.grey5,
  },
  emptyIcon: {
    marginBottom: 10,
    opacity: 0.5,
  },
  productEmptyText: {
    fontSize: 14,
    color: Colors.black5,
    marginTop: 8,
  },
});

export default React.memo(BrandItem);

BrandItem.propTypes = {
  data: PropTypes.object,
  index: PropTypes.number,
  isSearch: PropTypes.bool,
  onExpand: PropTypes.func,
  selectedBrand: PropTypes.array,
  loading: PropTypes.object,
  listProduct: PropTypes.object,
  limit: PropTypes.number,
  empty: PropTypes.object,
  loadingMore: PropTypes.object,
  skip: PropTypes.object,
  onSearchProducts: PropTypes.func,
  lastPayload: PropTypes.array,
  detailData: PropTypes.object,
  setLastPayload: PropTypes.func,
  customerAddress: PropTypes.object,
  navigation: PropTypes.object,
  accountGroupId: PropTypes.string,
  isValueSellOutOnly: PropTypes.bool,
};
